// src/api/collectionApi.ts

/* =========================
   TYPES
========================= */

export type CollectionDto = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  era?: string | null;
};

/** Paging chuẩn để UI dùng */
export type Paging = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

/**
 * Response backend có thể về:
 * {
 *   collection: {...},
 *   products: [...],
 *   pagination?: {...}
 *   // hoặc đôi khi trả thẳng:
 *   page, pageSize, total, totalPages
 * }
 */
export type CollectionAllResponse = {
  collection?: any;
  products?: any[];

  pagination?: Partial<Paging>;

  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
};

/* =========================
   API CORE
========================= */

function apiUrl() {
  const base = process.env.REACT_APP_API_URL;
  if (!base) throw new Error("REACT_APP_API_URL is not defined");
  return base.replace(/\/+$/, "");
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed: ${res.status}`);
  }

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    // tránh crash ngầm khi backend trả text/html
    return null as unknown as T;
  }

  return (await res.json()) as T;
}

/* =========================
   NORMALIZERS
========================= */

function normalizeCollection(raw: any): CollectionDto {
  return {
    id: Number(raw?.id ?? raw?.collectionId ?? 0),
    name: String(raw?.name ?? ""),
    slug: String(raw?.slug ?? ""),
    description: raw?.description ?? null,
    era: raw?.era ?? null,
  };
}

function normalizePaging(
  raw: any,
  fallbackPage: number,
  fallbackPageSize: number,
  fallbackTotal: number
): Paging {
  const p = Number(raw?.pagination?.page ?? raw?.page ?? fallbackPage ?? 1);
  const ps = Number(raw?.pagination?.pageSize ?? raw?.pageSize ?? fallbackPageSize ?? 12);
  const total = Number(raw?.pagination?.total ?? raw?.total ?? fallbackTotal ?? 0);

  const totalPages =
    Number(
      raw?.pagination?.totalPages ??
        raw?.totalPages ??
        Math.max(1, Math.ceil(total / Math.max(1, ps)))
    ) || 1;

  return {
    page: p || 1,
    pageSize: ps || 12,
    total: total || 0,
    totalPages: totalPages || 1,
  };
}

/* =========================
   COLLECTIONS (existing)
========================= */

/**
 * ✅ 1) LẤY LIST COLLECTIONS để render tabs
 * - Case A: backend trả thẳng array collections
 * - Case B: backend trả { data: Collection[] }
 * - Case C: backend trả { collection: {...}, products: [...] } => return [collection]
 */
export async function getCollections(params?: {
  page?: number;
  pageSize?: number;
  sort?: string; // "order" | "newest" | ...
}): Promise<CollectionDto[]> {
  const qs = new URLSearchParams();
  qs.set("page", String(params?.page ?? 1));
  qs.set("pageSize", String(params?.pageSize ?? 100));
  qs.set("sort", params?.sort ?? "order");

  const url = `${apiUrl()}/api/Collections/all?${qs.toString()}`;
  const raw: any = await fetchJson<any>(url);

  if (!raw) return [];

  // Case A: array collections
  if (Array.isArray(raw)) return raw.map(normalizeCollection);

  // Case B: { data: Collection[] }
  if (Array.isArray(raw?.data)) return raw.data.map(normalizeCollection);

  // Case C: { collection: {...}, products: [...] }
  if (raw?.collection) return [normalizeCollection(raw.collection)];

  return [];
}

/**
 * ✅ 2) LẤY PRODUCTS THEO COLLECTION ALL
 * Dùng cho trang Collection để render grid + paging.
 *
 * Trả về:
 *  - collection: CollectionDto | null
 *  - products: any[]
 *  - pagination: Paging
 */
export async function getCollectionAll(params?: {
  page?: number;
  pageSize?: number;
  sort?: string;
}): Promise<{
  collection: CollectionDto | null;
  products: any[];
  pagination: Paging;
}> {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 12;

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  qs.set("sort", params?.sort ?? "order");

  const url = `${apiUrl()}/api/Collections/all?${qs.toString()}`;
  const raw: any = await fetchJson<CollectionAllResponse>(url);

  const products = Array.isArray(raw?.products) ? raw.products : [];
  const collection = raw?.collection ? normalizeCollection(raw.collection) : null;

  const pagination = normalizePaging(raw, page, pageSize, products.length);

  return { collection, products, pagination };
}

/**
 * ✅ 3) LẤY CHI TIẾT COLLECTION THEO SLUG (collection + products)
 * GET /api/Collections/{slug}?page=1&pageSize=12&sort=...
 */
export async function getCollectionBySlug(
  slug: string,
  params?: { page?: number; pageSize?: number; sort?: string }
): Promise<{
  collection: CollectionDto | null;
  products: any[];
  pagination: Paging;
}> {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 12;

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  qs.set("sort", params?.sort ?? "order");

  const url = `${apiUrl()}/api/Collections/${encodeURIComponent(slug)}?${qs.toString()}`;
  const raw: any = await fetchJson<any>(url);

  const products = Array.isArray(raw?.products) ? raw.products : [];
  const collection = raw?.collection ? normalizeCollection(raw.collection) : null;
  const pagination = normalizePaging(raw, page, pageSize, products.length);

  return { collection, products, pagination };
}

/* =========================
   DROPS (new)
========================= */

export type DropNavDto = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  era: string;
  productCount?: number;
  createdAt?: string;
  kind?: string;
  season?: string | null;
};

export type DropDetailDto = CollectionDto & {
  kind?: string;
  season?: string | null;
  heroImageUrl?: string | null;
  shortDesc?: string | null;
  storyTitle?: string | null;
  storyBody?: string | null;
  featuredImageUrl?: string | null;
  launchAt?: string | null;
  endAt?: string | null;
  isLimited?: boolean;
  isRestock?: boolean;
};

function normalizeDropNav(raw: any): DropNavDto {
  return {
    id: Number(raw?.id ?? raw?.collectionId ?? 0),
    name: String(raw?.name ?? ""),
    slug: String(raw?.slug ?? ""),
    description: raw?.description ?? null,
    sortOrder: Number(raw?.sortOrder ?? 0),
    isActive: Boolean(raw?.isActive ?? true),
    era: String(raw?.era ?? "new"),
    productCount: Number(raw?.productCount ?? 0),
    createdAt: raw?.createdAt ? String(raw.createdAt) : undefined,
    kind: raw?.kind ? String(raw.kind) : undefined,
    season: raw?.season ?? null,
  };
}

function normalizeDropDetail(raw: any): DropDetailDto {
  const base = normalizeCollection(raw);
  return {
    ...base,
    kind: raw?.kind ? String(raw.kind) : undefined,
    season: raw?.season ?? null,
    heroImageUrl: raw?.heroImageUrl ?? null,
    shortDesc: raw?.shortDesc ?? null,
    storyTitle: raw?.storyTitle ?? null,
    storyBody: raw?.storyBody ?? null,
    featuredImageUrl: raw?.featuredImageUrl ?? null,
    launchAt: raw?.launchAt ?? null,
    endAt: raw?.endAt ?? null,
    isLimited: Boolean(raw?.isLimited ?? false),
    isRestock: Boolean(raw?.isRestock ?? false),
  };
}

/**
 * ✅ LẤY LIST DROPS để render tabs
 * GET /api/Collections/drops?era=new&isActive=true
 */
export async function getDropsNav(params?: { era?: string; isActive?: boolean }): Promise<DropNavDto[]> {
  const qs = new URLSearchParams();
  if (params?.era) qs.set("era", params.era);
  if (typeof params?.isActive === "boolean") qs.set("isActive", String(params.isActive));

  const url = `${apiUrl()}/api/Collections/drops${qs.toString() ? `?${qs.toString()}` : ""}`;
  const raw: any = await fetchJson<any>(url);

  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(normalizeDropNav);
  if (Array.isArray(raw?.data)) return raw.data.map(normalizeDropNav);

  return [];
}

/**
 * ✅ LẤY CHI TIẾT DROP + PRODUCTS theo SLUG
 * GET /api/Collections/{slug}?page=1&pageSize=9&sort=order
 *
 * Lưu ý: slug chính là "drop-01" (không phải DROP_01).
 */
export async function getDropDetail(
  slug: string,
  params?: { page?: number; pageSize?: number; sort?: string }
): Promise<{
  collection: DropDetailDto | null;
  products: any[];
  pagination: Paging;
}> {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 9;

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  qs.set("sort", params?.sort ?? "order");

  const url = `${apiUrl()}/api/Collections/${encodeURIComponent(slug)}?${qs.toString()}`;
  const raw: any = await fetchJson<any>(url);

  const products = Array.isArray(raw?.products) ? raw.products : [];
  const collection = raw?.collection ? normalizeDropDetail(raw.collection) : null;
  const pagination = normalizePaging(raw, page, pageSize, products.length);

  return { collection, products, pagination };
}

/**
 * (Tuỳ chọn) ✅ LẤY DROP MỚI NHẤT THEO ERA
 * GET /api/Collections/drops/latest?era=new&page=1&pageSize=9&sort=order
 */
export async function getLatestDrop(params?: { era?: string; page?: number; pageSize?: number; sort?: string }) {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 9;

  const qs = new URLSearchParams();
  qs.set("era", params?.era ?? "new");
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  qs.set("sort", params?.sort ?? "order");

  const url = `${apiUrl()}/api/Collections/drops/latest?${qs.toString()}`;
  const raw: any = await fetchJson<any>(url);

  const products = Array.isArray(raw?.products) ? raw.products : [];
  const collection = raw?.collection ? normalizeDropDetail(raw.collection) : null;
  const pagination = normalizePaging(raw, page, pageSize, products.length);

  return { collection, products, pagination };
}
